import { useId, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { inputClass, scrollRootOf } from "../../components/ui";
import { DOCS } from "../../docs";

/**
 * The documentation library: every customer-facing document under one
 * left-hand navigation, release notes included, so no page reads as an
 * orphan. The filter matches title and summary; four entries today, more
 * later, which is the point of building the shelf before it is full.
 */
export const LIBRARY = [
  ...DOCS.map((d) => ({ to: `/docs/${d.slug}`, title: d.title, summary: d.summary })),
  { to: "/docs/release-notes", title: "Release notes", summary: "What changed in each version of the portal and of the customer template." },
] as const;

/**
 * Where the shelf rests and the room it has, published on the grid as
 * --docs-shelf-top and --docs-shelf-room (#410, from Mermera #933's
 * useShelfRoom). The shelf is pinned at its resting place - the grid's own
 * top in the scroll container, which is the document in Starsach's shell,
 * so a banner above <main> is counted - and never taller than the scroll
 * container's view less everything in it that is not this grid (banners,
 * the top inset, the footer, the bottom padding). A sticky element only
 * stays put while its containing block has room below it; on a page
 * shorter than the shelf the grid was exactly the shelf's height, so
 * scrolling to the footer carried the shelf up with the page. Capped at
 * this height the shelf never needs to move - it scrolls inside itself.
 */
function useShelfRoom(ref: RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const grid = ref.current;
    if (!grid || typeof ResizeObserver === "undefined") return;
    const root = scrollRootOf(grid);
    const isDocument = root === document.documentElement;
    const publish = () => {
      const view = isDocument ? window.innerHeight : root.clientHeight;
      const room = view - (root.scrollHeight - grid.offsetHeight);
      const top = grid.getBoundingClientRect().top - (isDocument ? 0 : root.getBoundingClientRect().top) + root.scrollTop;
      grid.style.setProperty("--docs-shelf-room", `${Math.max(room, 240)}px`);
      grid.style.setProperty("--docs-shelf-top", `${Math.max(top, 0)}px`);
      // What follows the grid (the bottom padding, the footer): a page's own "On this page" rail is capped by it too,
      // or at the bottom of the page the grid's end pushes the rail up under the frozen header.
      grid.style.setProperty("--docs-below", `${Math.max(root.scrollHeight - top - grid.offsetHeight, 0)}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(root);
    observer.observe(grid);
    // A viewport that only grows taller resizes neither when the page is longer than it.
    window.addEventListener("resize", publish);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
    };
  }, [ref]);
}

export function DocsLayout() {
  const ref = useRef<HTMLDivElement>(null);
  useShelfRoom(ref);
  const [term, setTerm] = useState("");
  const id = useId();
  const needle = term.trim().toLowerCase();
  const shown = needle ? LIBRARY.filter((e) => `${e.title} ${e.summary}`.toLowerCase().includes(needle)) : LIBRARY;
  return (
    <div ref={ref} className="md:grid md:grid-cols-[14rem_minmax(0,1fr)] md:items-start md:gap-8">
      {/* Pinned where it rests and never taller than the room useShelfRoom measures, so it scrolls inside itself and not with the page (#410). */}
      <nav aria-label="Documentation" data-testid="docs-shelf" className="mb-6 md:sticky md:top-[var(--docs-shelf-top,2rem)] md:mb-0 md:max-h-[var(--docs-shelf-room,calc(100vh-4rem))] md:overflow-y-auto">
        {/* The filter stays at the top of the shelf while the shelf scrolls - opening a page far down the list would scroll it out of sight. */}
        <div className="bg-canvas pb-3 md:sticky md:top-0 md:z-10 dark:bg-neutral-950">
          <label htmlFor={id} className="sr-only">
            Filter documentation
          </label>
          <input id={id} type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Filter" className={`${inputClass} mt-0`} />
        </div>
        <ul className="flex gap-1 overflow-x-auto md:flex-col md:gap-0.5">
          {shown.map((e) => (
            <li key={e.to} className="shrink-0">
              <NavLink
                to={e.to}
                className={({ isActive }) => `block whitespace-nowrap border-l-2 py-1.5 pl-2.5 pr-2 text-sm leading-5 md:whitespace-normal hover:border-neutral-400 dark:hover:border-neutral-600 ${isActive ? "border-brand-text font-semibold text-brand-text" : "border-transparent text-neutral-700 dark:text-neutral-300"}`}
                title={e.summary}
              >
                {e.title}
              </NavLink>
            </li>
          ))}
          {shown.length === 0 && <li className="px-2.5 py-1.5 text-sm text-neutral-500">Nothing matches.</li>}
        </ul>
      </nav>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
